import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import {
  calendarMonthGrid,
  formatEtDate,
  monthLabel,
  parseEtDate,
  shiftMonth,
  WEEKDAYS_SHORT,
} from '@/lib/cashFlow';

type Props = {
  /** Controlled ISO value (`YYYY-MM-DD`); `''` renders an empty field. */
  value: string;
  /** Emitted (ISO) only when the typed/picked date is a real, *different* date. */
  onChangeIso: (iso: string) => void;
  /** Locked (display-only) — the input greys out and the picker won't open. */
  disabled?: boolean;
  className?: string;
  title?: string;
  id?: string;
  'aria-label'?: string;
};

/** The month a `YYYY-MM-DD` value opens the picker on; a blank/odd value opens on the
 *  current month (local clock — the browser is already the user's Europe/Tallinn zone). */
function monthOf(iso: string): { year: number; month: number } {
  const m = /^(\d{4})-(\d{2})-/.exec(iso);
  if (m) return { year: Number(m[1]), month: Number(m[2]) };
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * The app's single editable-date control: shows + accepts the Estonian
 * "PP.KK.AAAA" (DD.MM.YYYY) while storing ISO, AND offers a click-to-pick calendar
 * popover (the same Monday-first `calendarMonthGrid` the range picker uses — no date
 * logic of its own, L14). Typing commits on blur / Enter and reverts to the last
 * valid value on malformed input; picking a day commits immediately. The data layer
 * stays ISO; the UI stays DD.MM.YYYY (no native `type=date`, whose rendered format is
 * browser-locale dependent and not contractually DD.MM.YYYY). The popover is portalled
 * to `document.body` so it escapes the dialog's scroll/clip container.
 */
export function EtDateInput({ value, onChangeIso, disabled, className, title, id, 'aria-label': ariaLabel }: Props) {
  const [text, setText] = useState(() => formatEtDate(value));
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => monthOf(value));
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);

  // Re-sync when the controlled ISO changes from outside (e.g. a reschedule).
  useEffect(() => {
    setText(formatEtDate(value));
  }, [value]);

  const commit = () => {
    const iso = parseEtDate(text);
    if (iso) {
      setText(formatEtDate(iso));
      if (iso !== value) onChangeIso(iso);
    } else {
      setText(formatEtDate(value));
    }
  };

  const openCalendar = () => {
    setView(monthOf(value));
    const r = wrapRef.current!.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setOpen(true);
  };

  const pick = (iso: string) => {
    setText(formatEtDate(iso));
    if (iso !== value) onChangeIso(iso);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        disabled={disabled}
        value={text}
        placeholder="PP.KK.AAAA"
        aria-label={ariaLabel}
        title={title}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className={cn(
          'w-full rounded border border-border bg-background pr-8 text-foreground outline-none focus:border-primary/60',
          className,
        )}
      />
      <button
        type="button"
        aria-label="Ava kalender"
        disabled={disabled}
        onClick={openCalendar}
        className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <rect x="2" y="3" width="12" height="11" rx="1.5" />
          <path d="M2 6.5h12M5 1.5v2.5M11 1.5v2.5" strokeLinecap="round" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[60]">
            <button
              type="button"
              aria-label="Sulge kalender"
              className="absolute inset-0"
              onClick={() => setOpen(false)}
            />
            <div
              role="dialog"
              aria-label="Kuupäeva valik"
              style={{ top: pos.top, left: pos.left }}
              className="absolute w-64 rounded-lg border border-border bg-popover p-2 shadow-xl"
            >
              <div className="mb-1.5 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Eelmine kuu"
                  onClick={() => setView((v) => shiftMonth(v.year, v.month, -1))}
                  className="rounded px-2 py-0.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  ‹
                </button>
                <span className="text-xs font-semibold text-foreground">{monthLabel(view.year, view.month)}</span>
                <button
                  type="button"
                  aria-label="Järgmine kuu"
                  onClick={() => setView((v) => shiftMonth(v.year, v.month, 1))}
                  className="rounded px-2 py-0.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  ›
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {WEEKDAYS_SHORT.map((w) => (
                  <span key={w} className="py-0.5 text-center text-[9px] font-medium uppercase text-muted-foreground/70">
                    {w}
                  </span>
                ))}
                {calendarMonthGrid(view.year, view.month).map((cell) => (
                  <button
                    key={cell.iso}
                    type="button"
                    aria-label={cell.iso}
                    aria-current={cell.iso === value ? 'date' : undefined}
                    onClick={() => pick(cell.iso)}
                    className={cn(
                      'rounded py-1 text-xs tabular-nums transition-colors',
                      cell.inMonth ? 'text-foreground hover:bg-accent' : 'text-muted-foreground/40 hover:bg-accent/50',
                      cell.iso === value && 'bg-[hsl(var(--primary))] font-semibold text-[hsl(var(--primary-foreground))]',
                    )}
                  >
                    {cell.day}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
